/**
 * Settings → Users: create, edit, deactivate and delete people; issue API keys.
 *
 * Two things this screen is deliberate about.
 *
 * **Deactivate is the primary action, delete is not.** Deleting a user cascades to
 * everything they produced — conversations, batch jobs, prompts, image records —
 * and an organization usually still needs that work after the person has left.
 * Deactivating blocks access immediately, ends their open sessions, and keeps the
 * data. So delete is behind a typed confirmation and says what it destroys.
 *
 * **The API key secret is shown once.** It is hashed before it reaches the
 * database, so there is no "show again". The panel says so at the moment it
 * appears rather than leaving someone to discover it tomorrow.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { ApiError, organizationsApi, usersApi } from '../../api';
import {
  Badge,
  Button,
  CopyButton,
  EmptyState,
  ErrorState,
  Loading,
  Modal,
  Select,
  TextInput,
} from '../../components/common';
import { useAuth } from '../../context/AuthContext';
import { useRuntimeSettings } from '../../context/RuntimeSettingsContext';
import { useToast } from '../../context/ToastContext';

const ROLE_OPTIONS = [
  { value: 'user', label: 'User — own conversations only' },
  { value: 'admin', label: 'Admin — manages users, sees the whole organization' },
];

const SUPERADMIN_OPTION = {
  value: 'superadmin',
  label: 'Superadmin — also manages organizations',
};

export function UsersTab() {
  const { user: me, isAdmin, isSuperadmin, organization, refresh } = useAuth();
  const toast = useToast();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const roleOptions = useMemo(
    () => (isSuperadmin ? [...ROLE_OPTIONS, SUPERADMIN_OPTION] : ROLE_OPTIONS),
    [isSuperadmin],
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setUsers(await usersApi.list(search ? { search } : {}));
    } catch (caught) {
      setError(caught);
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    load();
  }, [load]);

  const patch = useCallback(
    async (user, changes, describe) => {
      try {
        await usersApi.update(user.id, changes);
        toast.success(describe);
        await load();
        if (user.id === me?.id) await refresh();
      } catch (caught) {
        toast.error(caught);
      }
    },
    [load, me, refresh, toast],
  );

  if (loading && users.length === 0) return <Loading label="Loading users…" />;
  if (error) return <ErrorState error={error} onRetry={load} />;

  return (
    <div className="settings-stack">
      {!isAdmin && (
        <p className="settings__note">
          You are signed in as a standard user, so this page shows your own account only. An
          administrator in {organization?.name ?? 'your organization'} can create and manage users.
        </p>
      )}

      <div className="settings__toolbar">
        <TextInput
          placeholder="Search by username, name or email"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          aria-label="Search users"
        />
        {isAdmin && (
          <Button variant="primary" onClick={() => setCreating(true)}>
            Add user
          </Button>
        )}
      </div>

      {users.length === 0 ? (
        <EmptyState title="No users match that search" glyph="◔" />
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>User</th>
              <th>Role</th>
              <th>Status</th>
              <th>Last seen</th>
              <th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.id} className={user.is_active ? '' : 'table__row--muted'}>
                <td>
                  <div className="table__primary">
                    {user.display_name || user.username}
                    {user.id === me?.id && <span className="table__you"> (you)</span>}
                  </div>
                  <div className="table__secondary">
                    {user.username}
                    {user.email ? ` · ${user.email}` : ''}
                  </div>
                </td>
                <td>
                  <Badge tone={user.role === 'user' ? 'default' : 'accent'}>{user.role}</Badge>
                </td>
                <td>
                  {user.is_api_user ? (
                    <Badge
                      tone="warn"
                      title="Created automatically by an organization API key. Owns data, cannot sign in."
                    >
                      API identity
                    </Badge>
                  ) : user.is_active ? (
                    <Badge tone="ok">active</Badge>
                  ) : (
                    <Badge tone="danger">deactivated</Badge>
                  )}
                </td>
                <td className="table__secondary">{formatWhen(user.last_seen_at)}</td>
                <td className="table__actions">
                  {isAdmin && (
                    <>
                      <Button size="sm" onClick={() => setEditing(user)}>
                        Edit
                      </Button>
                      {user.id !== me?.id && (
                        <Button
                          size="sm"
                          onClick={() =>
                            patch(
                              user,
                              { is_active: !user.is_active },
                              user.is_active
                                ? `${user.username} deactivated and signed out`
                                : `${user.username} reactivated`,
                            )
                          }
                          title={
                            user.is_active
                              ? 'Blocks access immediately and ends their sessions. Keeps their work.'
                              : 'Restores access.'
                          }
                        >
                          {user.is_active ? 'Deactivate' : 'Reactivate'}
                        </Button>
                      )}
                      {user.id !== me?.id && (
                        <Button size="sm" variant="danger" onClick={() => setDeleting(user)}>
                          Delete
                        </Button>
                      )}
                    </>
                  )}
                  {!isAdmin && user.id === me?.id && (
                    <Button size="sm" onClick={() => setEditing(user)}>
                      Edit
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {isAdmin && organization && <ApiKeysPanel organizationId={organization.id} />}

      {creating && (
        <CreateUserModal
          roleOptions={roleOptions}
          onClose={() => setCreating(false)}
          onCreated={async () => {
            setCreating(false);
            await load();
          }}
        />
      )}

      {editing && (
        <EditUserModal
          user={editing}
          roleOptions={roleOptions}
          canManage={isAdmin}
          isSelf={editing.id === me?.id}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await load();
          }}
        />
      )}

      {deleting && (
        <DeleteUserModal
          user={deleting}
          onClose={() => setDeleting(null)}
          onDeleted={async () => {
            setDeleting(null);
            await load();
          }}
        />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ create */
function CreateUserModal({ roleOptions, onClose, onCreated }) {
  const toast = useToast();
  // Settings → General, "Minimum password length".
  const minPasswordLength = useRuntimeSettings().effective.min_password_length || 10;
  const [form, setForm] = useState({
    username: '',
    display_name: '',
    email: '',
    password: '',
    role: 'user',
  });
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState(null);

  const set = (field) => (event) => setForm({ ...form, [field]: event.target.value });

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setProblem(null);
    try {
      await usersApi.create(form);
      toast.success(`${form.username} created`);
      await onCreated();
    } catch (caught) {
      setProblem(caught.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open title="Add user" onClose={onClose}>
      <form onSubmit={submit} className="form">
        <TextInput
          label="Username"
          value={form.username}
          onChange={set('username')}
          required
          autoFocus
        />
        <TextInput label="Display name" value={form.display_name} onChange={set('display_name')} />
        <TextInput label="Email" type="email" value={form.email} onChange={set('email')} />
        <TextInput
          label="Password"
          type="password"
          value={form.password}
          onChange={set('password')}
          hint={`At least ${minPasswordLength} characters. A passphrase of three or four words is stronger than a short mixed-case one and easier to type.`}
          required
        />
        <Select
          label="Role"
          value={form.role}
          onChange={set('role')}
          options={roleOptions.map((option) => ({ value: option.value, label: option.label }))}
        />
        {problem && <p className="form__error">{problem}</p>}
        <div className="form__actions">
          <Button type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={busy}>
            {busy ? 'Creating…' : 'Create user'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/* -------------------------------------------------------------------- edit */
function EditUserModal({ user, roleOptions, canManage, isSelf, onClose, onSaved }) {
  const toast = useToast();
  const [form, setForm] = useState({
    display_name: user.display_name || '',
    email: user.email || '',
    role: user.role,
    password: '',
  });
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState(null);

  const set = (field) => (event) => setForm({ ...form, [field]: event.target.value });

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setProblem(null);

    const patch = {};
    if (form.display_name !== (user.display_name || '')) patch.display_name = form.display_name;
    if (form.email !== (user.email || '')) patch.email = form.email;
    if (canManage && !isSelf && form.role !== user.role) patch.role = form.role;
    if (form.password) patch.password = form.password;

    if (Object.keys(patch).length === 0) {
      onClose();
      return;
    }

    try {
      await usersApi.update(user.id, patch);
      toast.success(
        patch.password
          ? `${user.username} updated — their other sessions were signed out`
          : `${user.username} updated`,
      );
      await onSaved();
    } catch (caught) {
      setProblem(caught.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open title={`Edit ${user.username}`} onClose={onClose}>
      <form onSubmit={submit} className="form">
        <TextInput
          label="Display name"
          value={form.display_name}
          onChange={set('display_name')}
          autoFocus
        />
        <TextInput label="Email" type="email" value={form.email} onChange={set('email')} />

        {canManage && !isSelf && (
          <Select
            label="Role"
            value={form.role}
            onChange={set('role')}
            options={roleOptions.map((option) => ({ value: option.value, label: option.label }))}
          />
        )}
        {isSelf && (
          <p className="form__hint">
            You cannot change your own role. Another administrator has to do it — otherwise any
            compromised account would be a route to admin.
          </p>
        )}

        {canManage && (
          <TextInput
            label="Set a new password"
            type="password"
            value={form.password}
            onChange={set('password')}
            placeholder="Leave blank to keep the current one"
            hint="Setting a password signs this user out everywhere."
          />
        )}

        {problem && <p className="form__error">{problem}</p>}
        <div className="form__actions">
          <Button type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={busy}>
            {busy ? 'Saving…' : 'Save changes'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/* ------------------------------------------------------------------ delete */
function DeleteUserModal({ user, onClose, onDeleted }) {
  const toast = useToast();
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState(null);

  const submit = async (event) => {
    event.preventDefault();
    if (typed !== user.username) return;
    setBusy(true);
    setProblem(null);
    try {
      await usersApi.remove(user.id);
      toast.success(`${user.username} and their data were deleted`);
      await onDeleted();
    } catch (caught) {
      setProblem(caught.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open title={`Delete ${user.username}?`} onClose={onClose}>
      <form onSubmit={submit} className="form">
        <p className="form__danger">
          This permanently deletes <strong>{user.username}</strong> and everything they produced:
          conversations, batch jobs and their results, saved prompts, and image records. It cannot
          be undone.
        </p>
        <p className="form__hint">
          If the person has simply left, <strong>deactivate</strong> them instead. That blocks
          access immediately and ends their sessions, but keeps the work — which the organization
          usually still needs.
        </p>
        <TextInput
          label={`Type ${user.username} to confirm`}
          value={typed}
          onChange={(event) => setTyped(event.target.value)}
          autoFocus
        />
        {problem && <p className="form__error">{problem}</p>}
        <div className="form__actions">
          <Button type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="danger" disabled={busy || typed !== user.username}>
            {busy ? 'Deleting…' : 'Delete permanently'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/* ---------------------------------------------------------------- API keys */
function ApiKeysPanel({ organizationId }) {
  const toast = useToast();
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [issued, setIssued] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setKeys(await organizationsApi.listKeys(organizationId));
    } catch (caught) {
      if (!(caught instanceof ApiError) || !caught.isForbidden) toast.error(caught);
    } finally {
      setLoading(false);
    }
  }, [organizationId, toast]);

  useEffect(() => {
    load();
  }, [load]);

  const issue = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      const created = await organizationsApi.createKey(organizationId, { name });
      setIssued(created);
      setName('');
      await load();
    } catch (caught) {
      toast.error(caught);
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (key) => {
    try {
      await organizationsApi.revokeKey(key.id);
      toast.success(`${key.prefix} revoked`);
      await load();
    } catch (caught) {
      toast.error(caught);
    }
  };

  return (
    <section className="settings__section">
      <h2 className="settings__heading">Organization API keys</h2>
      <p className="settings__note">
        These are how another system in your organization calls the API. Each request sends two
        headers: <code>X-API-Key</code> with the organization&apos;s key, and <code>X-User</code>{' '}
        with the username of whoever triggered it — so results are attributed to that person and
        they see their own history here. A username we have not seen before is created automatically
        as an API identity that cannot sign in.
      </p>

      <form className="settings__toolbar" onSubmit={issue}>
        <TextInput
          placeholder="What is this key for? e.g. star-rating web app"
          value={name}
          onChange={(event) => setName(event.target.value)}
          aria-label="API key name"
          required
        />
        <Button type="submit" variant="primary" disabled={busy || !name.trim()}>
          {busy ? 'Issuing…' : 'Issue key'}
        </Button>
      </form>

      {issued && (
        <div className="settings__reveal">
          <strong>Copy this key now — it is shown once.</strong>
          <p>
            It is hashed before it is stored, so it cannot be recovered. If it is lost, revoke it
            and issue another.
          </p>
          <div className="settings__secret">
            <code>{issued.key}</code>
            <CopyButton value={issued.key} label="Copy key" />
          </div>
          <Button size="sm" onClick={() => setIssued(null)}>
            I have stored it
          </Button>
        </div>
      )}

      {loading ? (
        <Loading label="Loading keys…" />
      ) : keys.length === 0 ? (
        <EmptyState title="No API keys yet" glyph="⚿">
          Issue one when a system in your organization needs to call the API.
        </EmptyState>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Prefix</th>
              <th>Status</th>
              <th>Used</th>
              <th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {keys.map((key) => (
              <tr
                key={key.id}
                className={key.is_active && !key.is_expired ? '' : 'table__row--muted'}
              >
                <td className="table__primary">{key.name || '(unnamed)'}</td>
                <td>
                  <code>{key.prefix}</code>
                </td>
                <td>
                  {key.is_expired ? (
                    <Badge tone="warn">expired</Badge>
                  ) : key.is_active ? (
                    <Badge tone="ok">active</Badge>
                  ) : (
                    <Badge tone="danger">revoked</Badge>
                  )}
                </td>
                <td className="table__secondary">
                  {key.use_count > 0
                    ? `${key.use_count} calls · last ${formatWhen(key.last_used_at)}`
                    : 'never used'}
                </td>
                <td className="table__actions">
                  {key.is_active && (
                    <Button size="sm" variant="danger" onClick={() => revoke(key)}>
                      Revoke
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

/* ----------------------------------------------------------------- helpers */
function formatWhen(iso) {
  if (!iso) return 'never';
  const when = new Date(iso);
  const seconds = Math.round((Date.now() - when.getTime()) / 1000);
  if (seconds < 90) return 'just now';
  if (seconds < 3600) return `${Math.round(seconds / 60)} min ago`;
  if (seconds < 86400) return `${Math.round(seconds / 3600)} h ago`;
  return when.toLocaleDateString();
}
